<!--
  File name: Login.vue
  Program Description:
    - This component contains the means to log in within the DBNMS system.
  Includes:
    - Login validation and request
    - Forgot password functionality
    - Log in with Microsoft option
    - One Time Pin Modal
    - Navigation to Sign Up page
    - Locked out validation
    - Handling internal and external login
-->
<template>
  <v-container
    class=" layout d-flex justify-center"
    style="margin-top: 7%"
  >
    <v-container
      class="fill-height container rounded"
      fluid
      :style="`background-color: ${$metaData.app.login.background}; width: 43%`"
    >
      <v-col
        class="padding"
      >
        <p class="signin pb-0">
          {{ signInTitle }}
        </p>
        <v-form lazy-validation>
          <v-text-field
            id="username"
            v-model.trim="username"
            label="Username"
            name="username"
            type="text"
            outlined
            background-color="white"
            :error-messages="genericError('username', 'Username')"
            @input="$v.username.$touch()"
            @blur="$v.username.$touch()"
            @keyup.enter="submit(false, $event)"
          />
          <v-text-field
            id="password"
            v-model.trim="password"
            label="Password"
            name="password"
            outlined
            background-color="white"
            :error-messages="genericError('password', 'Password')"
            :append-icon="$data.$meta.password.show ? 'mdi-eye' : 'mdi-eye-off'"
            :type="$data.$meta.password.show ? 'text' : 'password'"
            @input="$v.password.$touch()"
            @blur="$v.password.$touch()"
            @keyup.enter="submit(false, $event)"
            @click:append="$data.$meta.password.show = !$data.$meta.password.show"
          />
        </v-form>
        <v-row
          class="d-flex flex-row-reverse"
        >
          <v-col
            v-if="!isInternalLogin"
            class="pa-0"
            cols="5"
          >
            <router-link
              class="nav-link"
              to="PassReset"
            >
              <v-btn
                text
                block
                small
              >
                Forgot Password?
              </v-btn>
            </router-link>
          </v-col>
        </v-row>
        <v-row
          class="pt-4"
        >
          <v-col cols="12">
            <v-btn
              id="login-button"
              color="primary"
              class="my-3"
              type="button"
              block
              large
              :loading="$data.$meta.isLoading"
              @click="submit(false)"
            >
              Login
            </v-btn>
            <a
              v-if="isInternalLogin"
              :href="microsoftSsoLoginLink"
            >
              <v-btn
                id="login-button"
                color="primary"
                type="button"
                class="my-3"
                block
                large
                outlined
              >
                <v-icon class="mr-3">mdi-microsoft-windows</v-icon>Log in with Microsoft
              </v-btn>
            </a>
            <!-- OTP LOGIN -->
            <template v-if="backupDialog === false">
              <v-dialog
                v-model="dialog"
                max-width="500px"
                persistent
              >
                <v-card
                  class="elevation-12 px-5 pt-6 pb-11 rounded-lg"
                  tile
                >
                  <v-icon
                    id="close-view-button"
                    class="red--text closeBtn text--darken-5"
                    @click="closeModal('otp')"
                  >
                    mdi-close-circle
                  </v-icon>
                  <v-card-title
                    class="title"
                  >
                    <h1 class="primary--text text-h3 font-weight-bold">
                      Input your OTP
                    </h1>
                    <v-spacer />
                  </v-card-title>
                  <v-card-subtitle class="py-5 black--text text-subtitle-2">
                    <p>Enter the One-Time Login Pin we just sent to your email,<br> {{ username }}</p>
                  </v-card-subtitle>
                  <v-card-text>
                    <v-form
                      lazy-validation
                      @submit.prevent="verify(false)"
                    >
                      <v-text-field
                        id="otp-field"
                        v-model.trim="pin"
                        :disabled="$data.$meta.isLoading"
                        label="One-time pin"
                        outlined
                        :error-messages="genericError('pin', 'OTP')"
                        @input="$v.pin.$touch()"
                        @blur="$v.pin.$touch()"
                      />
                    </v-form>
                  </v-card-text>
                  <v-card-subtitle class="py-0 black--text font-weight-bold">
                    <p class="mb-3">
                      Your One-Time Login Pin will expire in <u> {{ otpExpiry }} </u>
                    </p>
                  </v-card-subtitle>
                  <v-card-actions class="d-flex flex-column">
                    <v-btn
                      id="verify-otp-btn"
                      block
                      class="py-6"
                      color="primary"
                      :disabled="$data.$meta.isLoading"
                      @click="verify(false)"
                    >
                      Verify
                    </v-btn>
                    <v-spacer />
                    <v-btn
                      id="resend-otp-btn"
                      :loading="$data.$meta.isLoading"
                      class="py-6 primary--text font-weight-bold"
                      text
                      @click="resend(false)"
                    >
                      Resend OTP
                    </v-btn>
                  </v-card-actions>
                  <v-row
                    v-if="!isInternalLogin"
                    class="mt-4"
                  >
                    <p class="mx-auto my-0 font-weight-black">
                      Can't access your email? <span
                        class="primary--text"
                        style="cursor: pointer; "
                        @click="backupDialog = true, closeModal('otp')"
                      >Login via Backup Code</span>
                    </p>
                  </v-row>
                </v-card>
              </v-dialog>
            </template>
            <!-- BACKUP CODE LOGIN -->
            <template v-if="dialog === false">
              <v-dialog
                v-model="backupDialog"
                max-width="650px"
                persistent
              >
                <v-card
                  class="elevation-12 px-5 pt-6 pb-11 rounded-lg"
                  tile
                >
                  <v-icon
                    id="close-view-button"
                    class="red--text closeBtn text--darken-5"
                    @click="closeModal('backupCode')"
                  >
                    mdi-close-circle
                  </v-icon>
                  <v-card-title
                    class="title"
                  >
                    <h1 class="primary--text text-h3 font-weight-bold">
                      Input Backup Code
                    </h1>
                    <v-spacer />
                  </v-card-title>
                  <v-card-subtitle class="py-5 black--text text-subtitle-2">
                    <p>Enter the Backup Code we sent to your email, {{ username }} upon signing up.</p>
                  </v-card-subtitle>
                  <v-card-text>
                    <v-form
                      lazy-validation
                      @submit.prevent="verifyBackupCode(false)"
                    >
                      <v-text-field
                        id="backupCode-field"
                        v-model.trim="backupCode"
                        :disabled="$data.$meta.isLoading"
                        label="Backup Code"
                        outlined
                        :error-messages="genericError('backupCode', 'Backup Code')"
                        @input="$v.backupCode.$touch()"
                        @blur="$v.backupCode.$touch()"
                      />
                    </v-form>
                  </v-card-text>
                  <v-card-actions class="d-flex flex-column">
                    <v-btn
                      id="backupCode-login-btn"
                      block
                      class="py-6"
                      color="primary"
                      :disabled="$data.$meta.isLoading"
                      @click="verifyBackupCode(false)"
                    >
                      Login
                    </v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </template>
          </v-col>
        </v-row>
        <v-row
          style="padding: 0 15px;"
          align="center"
          justify="center"
        >
          <v-col
            v-if="!isInternalLogin"
            cols="12"
            align="center"
          >
            Don't have an account?
            <router-link
              class="nav-link"
              to="signup"
            >
              <span class="text-decoration-underline">
                Sign up
              </span>
            </router-link>
          </v-col>
        </v-row>
      </v-col>
    </v-container>
  </v-container>
</template>

<script>
import { validationMixin, custom, validationErrors } from '@/utils/validations'
import { baseUrl } from '@/settings'
import { authSocket } from '@/socket'
const { username, pin, backupCode } = custom

export default {
  components: {
  },
  mixins: [validationMixin],
  data: () => ({
    username: '',
    password: '',
    pin: '',
    otpExpiry: '',
    backupCode: '',
    dialog: false,
    backupDialog: false,
    $meta: {
      password: {
        show: false
      },
      isLoading: false
    },
    microsoftSsoLoginLink: '',
    isInternalLogin: false,
    signInTitle: 'Login'
  }),
  validations: {
    username,
    password: custom.login.password,
    pin,
    backupCode
  },
  computed: {
    ...validationErrors
  },
  watch: {
    isInternalLogin (boolValue) {
      if (boolValue) {
        this.signInTitle = 'Internal Login'
      } else {
        this.signInTitle = 'Login'
      }
    }
  },
  created () {
    this.username = this.$route.params.username
    this.microsoftSsoLoginLink = `${baseUrl}/sso/azure/login`
    this.isInternalLogin = this.$store.state.lookups.isInternalLogin
  },
  mounted () {
    if (this.username) {
      this.showAlert({
        message: 'Please remember this username. You will use it every time you login to the system.',
        type: 'info'
      })
    }
    if (this.$route.params.message) {
      this.$store.dispatch('showAlert', {
        message: this.$route.params.message,
        type: 'error',
        override: true
      })
    }
  },
  methods: {
    // verify if username, password, and otp are correct
    // behaviour handling for when maximum number of sessions has been reached, when locked out, and when correct creds are used
    async verify (forceful) {
      this.$v.username.$touch()
      this.$v.password.$touch()
      this.$v.pin.$touch()
      const allValid = (!this.$v.username.$invalid && !this.$v.password.$invalid && !this.$v.pin.$invalid)
      if (allValid) {
        this.$data.$meta.isLoading = true
        const resp = await this.$s.verify(this.username, this.pin, forceful)
        this.$data.$meta.isLoading = false
        if (!resp.ok) {
          const { error } = this.$s
          if (error.includes('Maximum number of sessions has been reached.')) {
            this.$store.dispatch('showDialog', {
              title: 'Multiple Logins',
              message: 'You are currently logged in on another device. Do you want to log out all of your existing sessions?',
              callback: () => { this.submit(true) }
            })
            return
          }
          if (error.includes('User is now locked out.')) {
            this.closeModal('otp')
          }

          this.$store.dispatch('showAlert', {
            message: error,
            type: 'error'
          })
        } else {
          authSocket.disconnect()
          this.$router.push({
            name: 'AdminDashboard'
          })
        }
      }
    },
    // verify if backup code used is correct
    // behaviour handling when correct and wrong backup code is inputted
    async verifyBackupCode (forceful) {
      this.$v.username.$touch()
      this.$v.password.$touch()
      this.$v.backupCode.$touch()
      const allValid = (!this.$v.username.$invalid && !this.$v.password.$invalid && !this.$v.backupCode.$invalid)
      if (allValid) {
        this.$data.$meta.isLoading = true
        const resp = await this.$s.backupCodeLogin(this.username, this.backupCode, forceful)
        this.$data.$meta.isLoading = false
        if (!resp.ok) {
          const { error } = this.$s
          this.$store.dispatch('showAlert', {
            message: error,
            type: 'error'
          })

          if (error.includes('User is now locked out.')) {
            this.closeModal('backupCode')
            this.closeModal('otp')
          }
        } else {
          authSocket.disconnect()
          this.$router.push({
            name: 'AdminDashboard'
          })
        }
      }
    },
    // lose focus on event target
    loseFocus ($event) {
      $event.target.blur()
    },
    // close function for otp and backup code modal
    closeModal (modal) {
      if (modal === 'otp') {
        this.dialog = false
        this.pin = ''
        this.$v.pin.$reset()
      } else if (modal === 'backupCode') {
        this.backupDialog = false
        this.backupCode = ''
        this.$v.backupCode.$reset()
        this.dialog = true
      }
    },
    // resend otp code to email
    async resend (forceful) {
      const { $meta, ...creds } = this.$data
      $meta.isLoading = true
      const resp = await this.$s.loginWithOtp(creds, forceful)
      $meta.isLoading = false
      if (resp.ok) {
        this.$store.dispatch('showAlert', {
          message: resp.jData.message,
          type: 'success'
        })
      }
    },
    // submit username and password for login
    // behaviour handling when wrong credentials or when account is locked out
    // opens modal for otp input when correct credentials
    submit: async function (forceful, $event) {
      this.$v.username.$touch()
      this.$v.password.$touch()
      if (!this.$v.username.$invalid && !this.$v.password.$invalid) {
        if ($event) {
          this.loseFocus($event)
        }
        const { $meta, ...creds } = this.$data
        $meta.isLoading = true
        const loginCreds = {
          ...creds,
          loginType: this.isInternalLogin ? 'Internal' : 'External'
        }
        const resp = await this.$s.login(loginCreds, forceful)
        $meta.isLoading = false
        if (!resp.ok) {
          let { error } = this.$s
          const lockedMessage = ['User is locked out.', 'User is now locked out.']
          const isLocked = lockedMessage.some(message => error.includes(message))
          isLocked && (error = 'Account Locked. You can try to login again after 15 minutes.')

          const wrongCredentials = ['User credentials not found.', 'Incorrect password.']
          const hasWrongCreds = wrongCredentials.some(message => error.includes(message))
          hasWrongCreds && (error = 'Login failed. The username or password is incorrect. Please try again.')
          if (error === 'User document is not active.') { error = 'User is not active.' }
          this.$store.dispatch('showAlert', {
            message: error,
            type: 'error'
          })
        } else {
          if (forceful) {
            this.$router.push({ name: 'AdminDashboard' })
          }
          this.otpExpiry = resp.jData.session.otpExpiryString
          this.dialog = true
        }
      }
    }
  }
}
</script>

<style scoped>
  .container {
    padding: 0px;
    margin: 0px;
  }
  .signin {
    font-size: 40px;
    font-weight: 1000
  }
  .padding {
    padding: 30px 40px;
  }
  .layout {
    width: 100%;
    padding: 0;
    margin-right: auto;
    margin-left: auto;
    margin-top: auto;
    margin-bottom: auto;
  }
.v-dialog__content--active >>> {
  z-index: 202 !important;
}
.closeBtn{
  position:absolute;
  top: 1rem;
  right: 1.25rem;
}
</style>
